import styled from "@emotion/styled";
import { ExtraBoldText, formatBalance, formatPercentage, lightGrey5, TagInfo } from "@stepfinance/react";
import type { FC } from "react";

import { useLanguageContext } from "../../contexts/language/language";
import { getTokenByMint } from "../../utils/tokenInfo";
import { NumberLabel, VaultPopoverContainer, VaultPopoverSection } from "../shared";
import CellValue from "../Table/CellValue";
import { VaultEpochCountdown } from "./VaultEpochCountdown";

type VaultPopoverProps = {
  vaultTokenSymbol: string;
  vaultTokenPrice: number;
  underlyingTokenSymbol: string;
  underlyingPrice: number;
  depositTokenSymbol?: string; // only needed when vault token is denominated against the deposit/collateral (Vault)
  strikePrice?: number;
  optionType?: string;
  expiration: Date;
  vaultStrategy: number;
  amountStaked?: number;
  rewardMint?: string;
  rewardAPY?: number;
  voltVaultType?: number;
};

export const VaultPopoverContent: FC<VaultPopoverProps> = ({
  vaultTokenSymbol,
  vaultTokenPrice,
  underlyingTokenSymbol,
  underlyingPrice,
  depositTokenSymbol,
  strikePrice,
  optionType,
  expiration,
  vaultStrategy,
  amountStaked,
  rewardMint,
  rewardAPY,
  voltVaultType,
}) => {
  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: { vaults: vaultText },
        },
      },
    },
  } = useLanguageContext();

  const rewardTokenInfo = rewardMint ? getTokenByMint(rewardMint) : undefined;
  amountStaked = amountStaked ? amountStaked : 0;
  return (
    <VaultPopoverContainer>
      {amountStaked > 0 && (
        <VaultPopoverSection>
          <ExtraBoldText>{common.staked + " " + vaultTokenSymbol}</ExtraBoldText>
          <span>{formatBalance(amountStaked, 5)}</span>
        </VaultPopoverSection>
      )}
      {rewardTokenInfo && (
        <VaultPopoverSection>
          <ExtraBoldText>{common.reward + " " + common.token}</ExtraBoldText>
          <StakedRewardTokenInfo>
            <TagInfo useSpaceMono={false}>
              <SymbolCircle />
              {rewardTokenInfo.symbol.toUpperCase()}
            </TagInfo>
            <StakedRewardAPY>{formatPercentage(rewardAPY ? rewardAPY : 0)} APY</StakedRewardAPY>
          </StakedRewardTokenInfo>
        </VaultPopoverSection>
      )}
      <VaultPopoverSection>
        <ExtraBoldText>{vaultText.vaultToken}</ExtraBoldText>
        <span>
          <NumberLabel>1</NumberLabel> {vaultTokenSymbol} = <NumberLabel>{vaultTokenPrice?.toFixed(5)}</NumberLabel>{" "}
          {depositTokenSymbol ?? underlyingTokenSymbol}
        </span>
      </VaultPopoverSection>
      <VaultPopoverSection>
        <ExtraBoldText>
          {underlyingTokenSymbol} {headers.price}
        </ExtraBoldText>
        <CellValue cellType="value">{underlyingPrice}</CellValue>
      </VaultPopoverSection>
      {strikePrice && optionType && expiration ? (
        <VaultPopoverSection>
          <ExtraBoldText>{vaultText.friktionVaults.lastTradedOption}</ExtraBoldText>
          <span>
            <CellValue cellType="value">{strikePrice}</CellValue> {optionType}{" "}
            {expiration.toLocaleString("default", { month: "long" })} <NumberLabel>{expiration.getDate()}</NumberLabel>
          </span>
        </VaultPopoverSection>
      ) : (
        <>
          {voltVaultType === 0 && (
            <VaultPopoverSection>
              <ExtraBoldText>{vaultText.friktionVaults.lastTradedOption}</ExtraBoldText>
              <CellValue cellType="text">{vaultText.friktionVaults.notStartedYet}</CellValue>
            </VaultPopoverSection>
          )}
        </>
      )}
      <VaultPopoverSection>
        <ExtraBoldText>{vaultText.friktionVaults.autoCompoundingIn}</ExtraBoldText>
        <span>
          <VaultEpochCountdown isCrab={vaultStrategy === 3} />
        </span>
      </VaultPopoverSection>
    </VaultPopoverContainer>
  );
};

const StakedRewardAPY = styled.span`
  margin-left: 0.5rem;
`;

const StakedRewardTokenInfo = styled.span`
  display: flex;
  align-items: center;
`;

const SymbolCircle = styled.div`
  background: ${lightGrey5};
  border-radius: 8px;
  height: 16px;
  margin-right: 0.3rem;
  width: 16px;
`;
